import { useCallback, useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import {
  Activity,
  FileBadge,
  KeyRound,
  LayoutDashboard,
  Loader2,
  LogOut,
  ScrollText,
  ShieldCheck,
  Smartphone,
  SunMoon,
  UserCog,
} from 'lucide-react'
import { api, getToken, onSessionExpired, setToken, type Health, type SessionUser } from '@/lib/api'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AuthProvider, roleLabel, useAuth, type Capability } from '@/lib/auth'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { DevicesPage } from '@/pages/DevicesPage'
import { DeviceDetailPage } from '@/pages/DeviceDetailPage'
import { AuditPage } from '@/pages/AuditPage'
import { LicensesPage } from '@/pages/LicensesPage'
import { UsersPage } from '@/pages/UsersPage'
import { RolesPage } from '@/pages/RolesPage'
import { ActivationPage } from '@/pages/ActivationPage'
import { AccountPage } from '@/pages/AccountPage'

interface NavItem {
  to: string
  label: string
  icon: typeof LayoutDashboard
  end?: boolean
  /** 需要的角色能力；缺省表示所有已登录用户可见。 */
  cap?: Capability
}

const NAV: NavItem[] = [
  { to: '/', label: '总览', icon: LayoutDashboard, end: true, cap: 'device:read' },
  { to: '/devices', label: '设备管理', icon: Smartphone, cap: 'device:read' },
  { to: '/activation', label: '现场发码', icon: KeyRound, cap: 'activation:issue' },
  { to: '/licenses', label: '上位机授权记录', icon: FileBadge, cap: 'license:read' },
  { to: '/users', label: '账号管理', icon: UserCog, cap: 'user:read' },
  { to: '/roles', label: '角色与权限', icon: ShieldCheck, cap: 'role:read' },
  { to: '/audit', label: '操作日志', icon: ScrollText, cap: 'audit:read' },
]

/**
 * 强制改密界面。
 *
 * 管理员代建账号、或重置了某人的密码之后，对方必须先把它改成自己的密码 —— 否则
 * 「初始密码只有管理员知道」这件事会一直悬着。服务端在改密前会拒绝所有非
 * `/api/auth/*` 的接口（403），所以这里不能用「点侧边栏跳过」绕过；这个全屏界面
 * 只是让正常用户不必撞到 403。
 */
function ForcePasswordChange({
  user,
  onDone,
  onLogout,
}: {
  user: SessionUser
  onDone: (u: SessionUser) => void
  onLogout: () => void
}) {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (newPassword.length < 8) {
      toast.error('新密码至少 8 位')
      return
    }
    if (newPassword !== confirm) {
      toast.error('两次输入的新密码不一致')
      return
    }
    setBusy(true)
    try {
      const res = await api.changePassword(oldPassword, newPassword)
      // 服务端作废旧令牌并补发新的：不换掉的话下一次请求就 401
      setToken(res.token)
      toast.success('密码已更新')
      onDone({ ...user, mustChangePassword: false })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '修改失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid h-full place-items-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-base">请先修改初始密码</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={submit}>
            <div className="space-y-1.5">
              <Label htmlFor="force-old">当前密码</Label>
              <Input
                id="force-old"
                type="password"
                autoFocus
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="force-new">新密码（至少 8 位）</Label>
              <Input
                id="force-new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="force-confirm">确认新密码</Label>
              <Input
                id="force-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            <div className="flex items-center justify-between pt-1">
              <Button type="button" variant="ghost" size="sm" onClick={onLogout}>
                退出登录
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                提交
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

/** 未登录时跳转登录页。 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const loc = useLocation()
  if (!getToken()) return <Navigate to="/login" replace state={{ from: loc.pathname }} />
  return <>{children}</>
}

function Shell({ onLogout, health }: { onLogout: () => void; health: Health | null }) {
  const { can, user, role, roleName } = useAuth()
  const nav = NAV.filter((item) => !item.cap || can(item.cap))
  return (
    <div className="flex h-full">
      <aside className="flex w-60 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-card)]">
        {/*
          侧栏头部只用文字，不放 logo 图片。
          （曾用过 /icon-512.png：那张图是 RGBA 透明底 + 黑色字，而管理端是纯深色主题，
          必须垫白底才看得清，观感不佳，故移除。资源文件仍保留在 admin/public/ —— 它是
          PWA manifest 的图标之一，删了会让 manifest 里的图标 404。）
        */}
        <div className="px-5 py-5 leading-tight">
          <div className="text-sm font-semibold">东晟科工</div>
          <div className="text-[11px] text-[var(--color-muted-foreground)]">设备授权与发码中心</div>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 px-3">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-[var(--color-accent)] font-medium text-[var(--color-accent-foreground)]'
                    : 'text-[var(--color-muted-foreground)] hover:bg-[var(--color-accent)]/50 hover:text-[var(--color-foreground)]',
                )
              }
            >
              <Icon className="size-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t border-[var(--color-border)] p-3">
          {user && (
            <div className="flex items-center justify-between gap-2 px-1 pb-1 text-[11px] text-[var(--color-muted-foreground)]">
              <span className="truncate">{user.username}</span>
              <Badge variant={role === 'admin' ? 'default' : 'outline'}>
                {roleLabel(role, roleName)}
              </Badge>
            </div>
          )}
          {health && (
            <div className="flex items-center justify-between px-1 text-[11px] text-[var(--color-muted-foreground)]">
              <span className="flex items-center gap-1.5">
                <Activity className="size-3" />
                主密钥
              </span>
              <Badge variant={health.masterKeyConfigured ? 'success' : 'destructive'}>
                {health.masterKeyConfigured ? '已配置' : '未配置'}
              </Badge>
            </div>
          )}
          {/* 主题放在侧栏底部：任何已登录页面都够得着，不必绕进账号页 */}
          <div className="flex items-center justify-between px-1 text-[11px] text-[var(--color-muted-foreground)]">
            <span className="flex items-center gap-1.5">
              <SunMoon className="size-3" />
              主题
            </span>
            <ThemeToggle />
          </div>
          <NavLink
            to="/account"
            className={({ isActive }) =>
              cn(
                'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-[var(--color-accent)] font-medium text-[var(--color-accent-foreground)]'
                  : 'text-[var(--color-muted-foreground)] hover:bg-[var(--color-accent)]/50 hover:text-[var(--color-foreground)]',
              )
            }
          >
            <UserCog className="size-4" />
            账号与密码
          </NavLink>
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={onLogout}>
            <LogOut className="size-4" />
            退出登录
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-8 py-7">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/devices/:deviceId" element={<DeviceDetailPage />} />
            <Route path="/activation" element={<ActivationPage />} />
            <Route path="/licenses" element={<LicensesPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  )
}

export function App() {
  const navigate = useNavigate()
  const [ready, setReady] = useState(false)
  const [authed, setAuthed] = useState(false)
  // 角色能力都从 user 派生；此前 user 在登录/me() 之后被直接丢弃，界面上无从按角色区分
  const [user, setUser] = useState<SessionUser | null>(null)
  const [health, setHealth] = useState<Health | null>(null)

  const loadHealth = useCallback(() => {
    api
      .health()
      .then(setHealth)
      .catch(() => setHealth(null))
  }, [])

  useEffect(() => {
    loadHealth()
  }, [loadHealth])

  // 令牌过期（任意请求返回 401）时立即回到登录页，避免停留在空壳界面
  useEffect(() => {
    return onSessionExpired(() => {
      setAuthed(false)
      setUser(null)
      toast.error('登录状态已过期，请重新登录')
      navigate('/login', { replace: true })
    })
  }, [navigate])

  // 校验已存令牌是否仍然有效
  useEffect(() => {
    let alive = true
    const token = getToken()
    if (!token) {
      setReady(true)
      return
    }
    api
      .me()
      .then((res) => {
        if (!alive) return
        setUser(res.user)
        setAuthed(true)
      })
      .catch(() => {
        setToken(null)
        if (alive) {
          setAuthed(false)
          setUser(null)
        }
      })
      .finally(() => alive && setReady(true))
    return () => {
      alive = false
    }
  }, [])

  const handleLogout = useCallback(() => {
    setToken(null)
    setAuthed(false)
    setUser(null)
    navigate('/login')
  }, [navigate])

  if (!ready) {
    return (
      <div className="grid h-full place-items-center text-[var(--color-muted-foreground)]">
        <Loader2 className="size-5 animate-spin" />
      </div>
    )
  }

  return (
    <AuthProvider user={user}>
      <Routes>
        <Route
          path="/login"
          element={
            authed ? (
              <Navigate to="/" replace />
            ) : (
              <LoginPage
                onSuccess={(u) => {
                  setUser(u)
                  setAuthed(true)
                  navigate('/', { replace: true })
                }}
              />
            )
          }
        />
        <Route
          path="/*"
          element={
            <RequireAuth>
              {user?.mustChangePassword ? (
                <ForcePasswordChange user={user} onDone={setUser} onLogout={handleLogout} />
              ) : (
                <Shell onLogout={handleLogout} health={health} />
              )}
            </RequireAuth>
          }
        />
      </Routes>
    </AuthProvider>
  )
}
